import { css, html, LitElement, type TemplateResult } from "lit";
import { SignalWatcher } from "@lit-labs/signals";
import { customElement, property } from "lit/decorators.js";
import * as Styles from "../../styles.ts";
import * as Logger from "../../services/logger.ts";
import * as LibraryAttachments from "../../services/library-attachments.ts";
import { type LibraryNoteItem } from "../../../models/library-note.ts";
import { type AppStore } from "../app-store.ts";
import { renderRoute } from "../../../util/route.ts";
import * as Config from "../../client-config.ts";

// Components
import "../app/app-toolbar.ts";
import "../app/view-header.ts";
import "../app/loading.ts";
import "../dui/page-inset/dui-page-inset.ts";
import "../dui/scroll-area/dui-scroll-area.ts";
import "../dui/vstack/dui-vstack.ts";
import "../dui/hstack/dui-hstack.ts";
import "../dui/button/dui-button.ts";
import "../dui/icon/dui-icon.ts";
import "../dui/link/dui-link.ts";
import "../dui/trunc/dui-trunc.ts";

const logger = Logger.create({ source: "client/library-view" });

const styles = css`
  :host {
    display: flex;
    flex-direction: column;
    height: 100cqh;
    width: 100cqw;
  }

  .tile-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: var(--space-6);
  }

  .tile {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-5);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    background: transparent;
    color: var(--card-foreground);
    text-decoration: none;
    transition:
      border-color var(--duration-fast) ease,
      background var(--duration-fast) ease;
    cursor: pointer;
  }

  .tile:hover {
    border-color: color-mix(in oklch, var(--border), var(--foreground) 6%);
    background: color-mix(in oklch, transparent, var(--foreground) 4%);
  }

  .tile-title {
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-semibold);
    color: var(--foreground);
    line-height: var(--line-height-snug);
  }

  .tile-excerpt {
    font-size: var(--font-size-md);
    color: var(--muted-foreground);
    line-height: var(--line-height-snug);
  }

  .attachment-chips {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .filename {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
  }

  .filesize {
    color: var(--muted-foreground);
    font-size: var(--font-size-sm);
  }
`;

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const handleDownloadAttachment = async (
  sha256: string,
  filename: string,
  accessToken: string | undefined,
): Promise<void> => {
  if (!accessToken) {
    logger.error("Cannot download attachment: no access token");
    return;
  }

  const result = await LibraryAttachments.downloadLibraryAttachment(
    accessToken,
    sha256,
    filename,
  );

  if (!result.ok) {
    logger.error("Failed to download attachment", { error: result.error });
  }
};

const libraryNoteItemView = (
  item: LibraryNoteItem,
  accessToken: string | undefined,
): TemplateResult => {
  const attachments = item.attachments ?? [];

  const href = renderRoute(Config.LIBRARY_NOTE_URL, { slug: item.slug });

  return html`
    <dui-link class="tile" href="${href}">
      <div class="tile-title">${item.title}</div>
      <dui-trunc class="tile-excerpt" max-lines="3">${item.excerpt}</dui-trunc>
      ${attachments.length > 0
        ? html`
            <div class="attachment-chips">
              ${attachments.map((attachment) => {
                const onDownload = (e: Event) => {
                  e.stopPropagation();
                  handleDownloadAttachment(
                    attachment.sha256,
                    attachment.meta.filename,
                    accessToken,
                  );
                };

                return html`
                  <dui-button
                    variant="secondary"
                    size="sm"
                    @click="${onDownload}"
                  >
                    <dui-hstack gap="2">
                      <dui-icon icon="attachment"></dui-icon>
                      <span class="filename">${attachment.meta.filename}</span>
                      <span class="filesize"
                        >${formatFileSize(attachment.meta.size_bytes)}</span
                      >
                    </dui-hstack>
                  </dui-button>
                `;
              })}
            </div>
          `
        : ""}
    </dui-link>
  `;
};

/**
 * Library view component that displays library notes.
 */
@customElement("library-view")
export class LibraryViewElement extends SignalWatcher(LitElement) {
  static override styles = [Styles.base, styles];

  @property({ attribute: false })
  accessor store: AppStore | undefined = undefined;

  override render(): TemplateResult {
    if (!this.store) {
      return html``;
    }

    const state = this.store.get();
    const accessToken = state.session?.access_token;

    // Loading state
    if (state.libraryNotesLoading) {
      return html`<app-toolbar></app-toolbar><loading-view></loading-view>`;
    }

    // Error state
    if (state.libraryNotesError) {
      return html`
        <app-toolbar></app-toolbar>
        <dui-page-inset>
          <view-header>Library</view-header>
          <p>Error loading library notes: ${state.libraryNotesError}</p>
        </dui-page-inset>
      `;
    }

    const noteItemsArray = Array.from(state.libraryNoteItems.values());

    // Empty state
    if (noteItemsArray.length === 0) {
      return html`
        <app-toolbar></app-toolbar>
        <dui-page-inset>
          <view-header>Library</view-header>
          <p>No library notes found.</p>
        </dui-page-inset>
      `;
    }

    // Success state with data
    return html`
      <app-toolbar></app-toolbar>
      <dui-scroll-area fade>
        <dui-page-inset>
          <view-header>Library</view-header>
          <div class="tile-grid">
            ${noteItemsArray.map((item) =>
              libraryNoteItemView(item, accessToken),
            )}
          </div>
        </dui-page-inset>
      </dui-scroll-area>
    `;
  }
}
